/**
 * The ring of a circular gauge (`accessoryCircularCapacity`,
 * `accessoryCircular`), an SVG the layout places over the gauge's square
 * (the `ring` chrome rect). In a 100-unit box: a capacity gauge is a full
 * track with the value filled clockwise from the top; an `accessoryCircular`
 * gauge is an arc of 270° open at the bottom, filled from its start to the
 * value. The value is the tint (`--sb-tint`, else the primary color), the
 * track the same at 35%, with round caps (styles.css).
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

/** The ring's stroke, in units of the 100-unit box (about 6 points at 58). */
const STROKE = 10;
const RADIUS = 50 - STROKE / 2;

/** The part of the circle an `accessoryCircular` gauge's arc covers. */
const OPEN_ARC = 0.75;

export function isCircularGauge(style: string | undefined): boolean {
  return style === 'accessoryCircular' || style === 'accessoryCircularCapacity';
}

/**
 * A ring with its track and value circles, in a box the layout places (and
 * hides, as an HTML element can be), hidden until laid out.
 */
export function createGaugeRing(): HTMLElement {
  const box = document.createElement('div');
  box.className = 'sb-gauge-ring';
  box.hidden = true;
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('aria-hidden', 'true');
  for (const part of ['track', 'value']) {
    const circle = document.createElementNS(SVG_NS, 'circle');
    circle.setAttribute('class', `sb-gauge-ring-${part}`);
    circle.setAttribute('cx', '50');
    circle.setAttribute('cy', '50');
    circle.setAttribute('r', String(RADIUS));
    circle.setAttribute('fill', 'none');
    circle.setAttribute('stroke-width', String(STROKE));
    // Dashes in hundredths of the circle.
    circle.setAttribute('pathLength', '100');
    svg.append(circle);
  }
  box.append(svg);
  return box;
}

/** The dash arrays and rotation that draw `value` (0...1) in `style`. */
export function gaugeRingGeometry(style: string, value: number): { track: string; value: string; rotation: number } {
  const v = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
  const round = (n: number) => Math.round(n * 1000) / 1000;
  if (style === 'accessoryCircular') {
    const arc = OPEN_ARC * 100;
    // From 135° (bottom left), clockwise over the top to 45° (bottom right).
    return { track: `${arc} 100`, value: `${round(arc * v)} 100`, rotation: 135 };
  }
  // Capacity: the whole circle, the value from 12 o'clock.
  return { track: '100 0', value: `${round(100 * v)} 100`, rotation: -90 };
}

export function updateGaugeRing(box: HTMLElement, style: string, value: number): void {
  const geometry = gaugeRingGeometry(style, value);
  const svg = box.firstElementChild;
  if (!svg) return;
  const [track, fill] = Array.from(svg.children) as SVGCircleElement[];
  for (const circle of [track, fill]) circle?.setAttribute('transform', `rotate(${geometry.rotation} 50 50)`);
  track?.setAttribute('stroke-dasharray', geometry.track);
  fill?.setAttribute('stroke-dasharray', geometry.value);
  // A zero value draws nothing (a round cap would show a dot).
  fill?.setAttribute('visibility', geometry.value.startsWith('0 ') ? 'hidden' : 'visible');
}
